<script setup lang="ts">
import MainContent from '~/components/influencer/profile/CuratorProfileMainContent.vue'

import { useCuratorMatchingTags } from '~/composables/useCuratorMatchingTags'
import { useCuratorProfileAbout } from '~/composables/useCuratorProfileAbout'

import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
}

const props = defineProps<Props>()
const hasMounted = ref(false)

const {
  matchingGenres,
  matchingLanguages,
  matchingNationalities,
  matchingTrackRelease,
  showMatchingTags,
  mismatches,
} = useCuratorMatchingTags(() => props.influencer)
const { genresConfiguration, showVerifiedBadge, hasBadgeTags } =
  useCuratorProfileAbout(() => props.influencer)

onMounted(() => {
  hasMounted.value = true
})
</script>

<template>
  <div
    class="tw-relative tw-flex tw-w-full tw-flex-col"
    :class="{ 'tw-gap-10': hasMounted }"
  >
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <Transition name="fade" mode="out-in">
        <LazyNeverInfluencerProfileAboutMatchingTags
          v-if="showMatchingTags"
          :genres="matchingGenres"
          :nationalities="matchingNationalities"
          :languages="matchingLanguages"
          :track-release="matchingTrackRelease"
          hydrate-never
        />
      </Transition>
    </ClientOnly>
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <Transition name="fade" mode="out-in">
        <div v-if="mismatches.length" class="tw-flex tw-flex-col tw-gap-4">
          <div
            v-for="{ mismatchText, weakMismatch } in mismatches"
            :key="mismatchText"
          >
            <LazyNeverInfluencerProfileAboutMismatchInfo
              :key="mismatchText"
              :mismatch-text="mismatchText"
              class="mismatchInfo"
              :is-error="!weakMismatch"
              hydrate-never
            />
          </div>
        </div>
      </Transition>
    </ClientOnly>
    <!-- Sm Mobile only -->
    <div class="tw-relative tw-flex tw-w-full tw-flex-col md:tw-hidden">
      <template v-if="hasBadgeTags">
        <LazyNeverInfluencerProfileAboutInfo
          v-if="hasBadgeTags"
          :influencer="influencer"
          hydrate-never
        />
        <hr
          class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300 first:tw-hidden"
        />
      </template>
      <LazyNeverInfluencerProfileAboutActivity
        hydrate-never
        :influencer="influencer"
      />
      <template v-if="showVerifiedBadge">
        <hr
          class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300 first:tw-hidden"
        />
        <LazyIdleInfluencerProfileAboutVerifiedProfileBadge
          v-if="showVerifiedBadge"
          hydrate-on-idle
        />
      </template>
    </div>
    <!-- Tablet only -->
    <div class="tw-relative tw-hidden tw-w-full md:tw-flex">
      <div v-if="hasBadgeTags" class="tw-w-6/12 tw-pr-6">
        <LazyNeverInfluencerProfileAboutInfo
          v-if="hasBadgeTags"
          :influencer="influencer"
          hydrate-never
        />
      </div>
      <div class="tw-w-6/12">
        <LazyNeverInfluencerProfileAboutActivity
          :influencer="influencer"
          hydrate-never
        />
        <LazyIdleInfluencerProfileAboutVerifiedProfileBadge
          v-if="showVerifiedBadge"
          class="tw-mt-6"
          hydrate-on-idle
        />
      </div>
    </div>
    <MainContent
      :influencer="influencer"
      :genres-configuration="genresConfiguration"
    >
      <template #post-content>
        <div
          v-if="
            influencer.spotify_playlists.length &&
            influencer.media_links.spotify.length > 0
          "
          class="tw-mt-6"
        >
          <LazyVisibleInfluencerProfileSpotifyPlaylistDisplayCollection
            :spotify-playlists="influencer.spotify_playlists"
            hydrate-on-visible
          />
        </div>
      </template>
    </MainContent>
  </div>
</template>
